import {
  InteractiveContainer,
  InteractiveContainerProps,
  Tooltip,
  Menu,
} from "@jugl-web/ui-components";
import { cx, useTranslations, assignRefs } from "@jugl-web/utils";
import { forwardRef } from "react";
import { ReactComponent as SortIcon } from "./assets/sort.svg";
import { ReactComponent as SortAscendingIcon } from "./assets/sortAscending.svg";
import { ReactComponent as SortDescendingIcon } from "./assets/sortDescending.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

interface TableSortingButtonProps extends InteractiveContainerProps {
  sortingOrder: "asc" | "desc" | null;
  sortingType?: "a-z" | "1-9" | "first-last";
  isMenu?: boolean;
  orderNumber?: number | null;
  onSortSelect?: (sortingOrder: "asc" | "desc" | null) => void;
}

export const TableSortingButton = forwardRef<
  HTMLElement,
  TableSortingButtonProps
>(
  (
    {
      sortingOrder,
      sortingType,
      isMenu,
      orderNumber,
      className,
      onSortSelect,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslations();

    const sortingTypeSwitchButton = {
      "a-z": {
        from: "A",
        to: "Z",
      },
      "1-9": {
        from: "1",
        to: "9",
      },
      "first-last": {
        from: t({ id: "common.first", defaultMessage: "First" }),
        to: t({ id: "common.last", defaultMessage: "Last" }),
      },
    };

    const mapSortingTypeToLabels = (sortType: "a-z" | "1-9" | "first-last") =>
      sortingTypeSwitchButton[sortType];

    const sortType = mapSortingTypeToLabels(sortingType || "a-z");

    const tooltipContent = (
      <>
        {sortingOrder === "asc" &&
          t({
            id: "table-sorting-button.sorting-order-tooltip.asc",
            defaultMessage: "Ascending",
          })}
        {sortingOrder === "desc" &&
          t({
            id: "table-sorting-button.sorting-order-tooltip.desc",
            defaultMessage: "Descending",
          })}

        {!sortingOrder &&
          t({
            id: "table-sorting-button.sorting-order-tooltip",
            defaultMessage: "Add Sorting",
          })}
      </>
    );

    if (isMenu) {
      return (
        <Tooltip
          renderTrigger={({ props: tooltipProps, ref: tooltipRef }) => (
            <div {...tooltipProps} ref={tooltipRef}>
              <Menu
                placement="bottom"
                className="p-4 font-secondary"
                selectedId={sortingOrder ?? undefined}
                sections={[
                  [
                    {
                      id: "asc",
                      className: cx("rounded-lg", {
                        "bg-[#EFF8FF]": sortingOrder === "asc",
                      }),
                      label: t({
                        id: "sort.asc",
                        defaultMessage: "Sort Ascending",
                      }),
                      onSelect: (_, close) => {
                        onSortSelect?.("asc");
                        close();
                      },
                      endSlot: (
                        <div
                          className={cx(
                            "flex items-center gap-1 text-[10px] text-dark-500",
                            {
                              "text-primary": sortingOrder === "asc",
                            }
                          )}
                        >
                          {sortType.from} <ArrowIcon />
                          {sortType.to}
                        </div>
                      ),
                    },
                    {
                      id: "desc",
                      className: cx("rounded-lg mt-4", {
                        "bg-[#EFF8FF]": sortingOrder === "desc",
                      }),
                      label: t({
                        id: "sort.desc",
                        defaultMessage: "Sort Descending",
                      }),
                      endSlot: (
                        <div
                          className={cx(
                            "flex items-center gap-1 text-[10px] text-dark-500",
                            {
                              "text-primary": sortingOrder === "desc",
                            }
                          )}
                        >
                          {sortType.to} <ArrowIcon />
                          {sortType.from}
                        </div>
                      ),
                      onSelect: (_, close) => {
                        onSortSelect?.("desc");
                        close();
                      },
                    },
                    {
                      id: "remove",
                      className: cx("rounded-lg mt-4"),
                      label: t({
                        id: "sort.remove",
                        defaultMessage: "Remove Sorting",
                      }),
                      endSlot: (
                        <span className="text-xs text-[#A7AEB0]">
                          <CloseIcon />
                        </span>
                      ),
                      onSelect: (_, close) => {
                        onSortSelect?.(null);
                        close();
                      },
                    },
                  ],
                ]}
                renderTrigger={({ Trigger, triggerRef }) => (
                  <Trigger
                    ref={assignRefs([
                      triggerRef as React.ForwardedRef<HTMLButtonElement>,
                    ])}
                    className={cx(
                      "h-6 w-6 rounded-full bg-transparent text-center text-[#A7AEB0] transition-all duration-75 hover:text-[#6192BE]",
                      className
                    )}
                    as={InteractiveContainer}
                  >
                    <div className="flex items-center gap-1">
                      {sortingOrder === "asc" ? (
                        <SortAscendingIcon />
                      ) : sortingOrder === "desc" ? (
                        <SortDescendingIcon />
                      ) : (
                        <SortIcon />
                      )}
                      {orderNumber && (
                        <span className="text-xs font-semibold text-[#6192BE]">
                          #{orderNumber}
                        </span>
                      )}
                    </div>
                  </Trigger>
                )}
              />
            </div>
          )}
        >
          {tooltipContent}
        </Tooltip>
      );
    }

    // Default: just the tooltip button
    return (
      <Tooltip
        renderTrigger={({ props: tooltipProps, ref: tooltipRef }) => (
          <InteractiveContainer
            ref={assignRefs([tooltipRef, ref])}
            {...tooltipProps}
            className={cx(
              "h-6 w-6 rounded-full bg-transparent text-center text-[#A7AEB0] transition-all duration-75 hover:bg-[#2196F31A] hover:text-[#6192BE]",
              className
            )}
            {...props}
          >
            <div className="flex items-center gap-1">
              {sortingOrder === "asc" ? (
                <SortAscendingIcon />
              ) : sortingOrder === "desc" ? (
                <SortDescendingIcon />
              ) : (
                <SortIcon />
              )}
              {orderNumber && (
                <span className="text-xs font-semibold text-[#6192BE]">
                  #{orderNumber}
                </span>
              )}
            </div>
          </InteractiveContainer>
        )}
      >
        {tooltipContent}
      </Tooltip>
    );
  }
);
